import React, { ReactNode } from 'react';
import { Stack, Text, List, ListItem, Columns, Column } from '@origin-digital/ods-core';
import { IEFixedAlert } from '../utils/IEFixedAlert';
import { RenderDuring } from '../utils/RenderDuring';
import {
  BLACK_FRIDAY,
  SalesFormEDRPerkAccordionType,
  StaticPerkAccordionType,
} from '../../typings';

const defaultTitle = (
  <Text weight="medium" tone="neutralDark" data-id="rewards-bonus-title-default">
    Origin Everyday Rewards Plan
  </Text>
);

const blackFriday2024Title = (
  <Text weight="medium" tone="neutralDark" data-id="rewards-bonus-title-open">
    Triple your Everyday Rewards points with Black Friday
  </Text>
);

const alwaysOnShortContent = (
  <Text data-id="rewards-bonus-content-default">
    Everyday Rewards member? Add your membership number to earn 5,000 bonus sign up points and earn
    1 point for every $1 (incl GST) bill charge. Origin Go Zero Electricity starts from $1.50 per
    week, and Origin Go Zero Natural Gas starts from $1 per week.
  </Text>
);

const blackFriday2024ShortContent = (
  <Text data-id="rewards-bonus-content-default">
    Everyday Rewards member? Add your membership number and turbo charge your Everyday Rewards
    points this Black Friday. New residential customers can earn 15,000 points for electricity and
    15,000 for natural gas.
  </Text>
);

const alwaysOnContent = (
  <Stack space="small">
    <Text>Get points for signing up:</Text>
    <List space="xsmall">
      <ListItem>New customers get 5,000 points for electricity and 5,000 points for gas.</ListItem>
      <ListItem>
        Renewing customers get 2,500 points for electricity and 2,500 points for gas.
      </ListItem>
    </List>
    <Text>Plus, collect 1 point for every $1 you spend on your usage and supply (incl GST).</Text>
    <Text>You'll need your Everyday Rewards number to sign up.</Text>
  </Stack>
);

// The EDR content for Black Friday 2024 MUST be rollback to [Always on] as in Figma after the campaign ends
const blackFriday2024Content = (
  <List space="xsmall">
    <ListItem>New customers get 5,000 points for electricity and 5,000 points for gas.</ListItem>
    <ListItem>
      Renewing customers get 2,500 points for electricity and 2,500 points for gas.
    </ListItem>
    <ListItem>
      Renewing your Everyday Rewards Variable plan? Collect a one-off bonus of 2,500 Everyday
      Rewards points for each energy type.
    </ListItem>
    <ListItem>
      Earn 1 point for every $1 charged for usage and supply (incl. GST). Redeem for money off your
      Woolies shop and more.
    </ListItem>
    <ListItem>Bonus points for signing up to Origin Go Zero carbon offset add-ons.</ListItem>
  </List>
);

const authTitle = (
  <Text weight="medium" tone="neutralDark" data-id="rewards-bonus-title-default">
    Power up your Everyday Rewards points
  </Text>
);
const authAlwaysOnContent = (
  <Text data-id="rewards-bonus-content-default">
    Everyday Rewards member? Add your membership number and power up your Everyday Rewards points.
    Earn 2,500 points for electricity and 2,500 for natural gas.
  </Text>
);

interface PromoBoxProps {
  variant?: 'default' | 'open' | 'auth';
}

export const EverydayRewardsVariableGasPRCHPromoBox = ({ variant = 'default' }: PromoBoxProps) => {
  // content for new EDR component in sales-monorepo
  const promoBoxOpenContent = (
    <Stack space="medium" data-id="rewards-bonus-content-open">
      <RenderDuring
        showDefault={alwaysOnContent}
        showDuring={blackFriday2024Content}
        fromDate={BLACK_FRIDAY.startDate}
        endDate={BLACK_FRIDAY.endDate}
      />
    </Stack>
  );

  // content for OLD EDR component in auth renew or sales forms (cross sell)
  const defaultVariantContent = (
    <IEFixedAlert
      icon={false}
      tone="promoteC"
      data-id="everyday-rewards-promo-box"
      data-testid="everyday-rewards-promo-box"
      muiProps={{
        style: {
          maxWidth: '100%',
        },
      }}
    >
      <Stack space="xsmall">
        <Columns space="medium">
          <Column>
            <Stack space="xxsmall">
              {variant == 'default' && (
                <RenderDuring
                  showDefault={defaultTitle}
                  showDuring={blackFriday2024Title}
                  fromDate={BLACK_FRIDAY.startDate}
                  endDate={BLACK_FRIDAY.endDate}
                />
              )}
              {variant == 'auth' && authTitle}
            </Stack>
          </Column>
          <Column width="content" alignX="right">
            <img
              src="https://res.cloudinary.com/originenergy/image/upload/v1606344623/43faa35315f4347eb9962d54418ce63f924a0150.png"
              alt="Everyday Rewards"
              height="44"
            />
          </Column>
        </Columns>
        {variant == 'default' && (
          <RenderDuring
            showDefault={alwaysOnShortContent}
            showDuring={blackFriday2024ShortContent}
            fromDate={BLACK_FRIDAY.startDate}
            endDate={BLACK_FRIDAY.endDate}
          />
        )}
        {variant == 'auth' && authAlwaysOnContent}
      </Stack>
    </IEFixedAlert>
  );

  // content for new EDR sales-monorepo component in compare plans
  const openVariantContent = (
    <Stack
      space="large"
      data-id="everyday-rewards-promo-box-open-variant"
      data-testid="everyday-rewards-promo-box-open-variant"
    >
      {promoBoxOpenContent}
    </Stack>
  );

  return <>{variant === 'open' ? openVariantContent : defaultVariantContent}</>;
};

export const EverydayRewardsSalesFormPromoBox = (_edrPoints: string, _fuelType: string) => {
  return (
    <Text data-id="edr-perk-sales-form-body">
      Plus, collect 1 Everyday Rewards point for ever $1 (inc GST) charged for every usage and
      supply
    </Text>
  );
};

/* EDR Perk */
const EDRPerkBody: ReactNode = <EverydayRewardsVariableGasPRCHPromoBox variant="open" />;

const EDRPerkSummary: ReactNode = <Text weight="medium">Up to 2,500 Everyday Rewards points</Text>;

export const EDRPerkSalesFormSummary = (edrPoints: string, fuelType: string) => {
  return (
    <Text weight="medium" data-id="edr-perk-sales-form-summary">
      {edrPoints} Everyday Rewards points for {fuelType}
    </Text>
  );
};

const EDRPerkIcon =
  'https://res.cloudinary.com/originenergy/image/upload/v1728624760/spot-illustrations/spot_edr-card-simplified.svg';

const EDRPerkAccordionId = 'edr-perk-accordion';

export const EDRPerkGasPRCHContent: StaticPerkAccordionType = {
  accordionSummary: EDRPerkSummary,
  accordionIcon: EDRPerkIcon,
  accordionBody: EDRPerkBody,
  accordionDataId: EDRPerkAccordionId,
};

export const EDRPerkElectricitySalesFormContent: SalesFormEDRPerkAccordionType = {
  accordionSummary: EDRPerkSalesFormSummary,
  accordionIcon: EDRPerkIcon,
  accordionBody: EverydayRewardsSalesFormPromoBox,
  accordionDataId: EDRPerkAccordionId,
};
